import type { ElasticLike, LogLevel, LogMeta, TrackingDocument } from './types'

export function handleElastic(
  elastic: ElasticLike | undefined,
  provider: string,
  stage: string,
  version: string | undefined,
  namespace: string,
  level: LogLevel,
  message: string,
  meta: LogMeta
): void {
  if (!elastic || !meta.index) {
    return
  }

  const doc = buildTrackingDocument(provider, stage, version, namespace, level, message, meta)
  const index = getTrackingIndexName(stage)

  // Fire-and-forget: never block or throw
  elastic.index({ index, body: doc as unknown as Record<string, unknown> }).catch((err) => {
    // Use console.error directly to avoid recursive logging
    console.error('[@paintscout/logger] Failed to write to ES tracking index:', err?.message || err)
  })
}

function buildTrackingDocument(
  provider: string,
  stage: string,
  version: string | undefined,
  namespace: string,
  level: LogLevel,
  message: string,
  meta: LogMeta
): TrackingDocument {
  const doc: TrackingDocument = {
    provider,
    namespace,
    level,
    message,
    timestamp: new Date().toISOString(),
    stage
  }

  if (version) doc.version = version
  if (meta.companyId) doc.companyId = meta.companyId as string
  if (meta.userId) doc.userId = meta.userId as string
  if (meta.category) doc.category = meta.category as string
  if (meta.action) doc.action = meta.action as string
  if (meta.entityType) doc.entityType = meta.entityType as string
  if (meta.entityId) doc.entityId = meta.entityId as string
  if (meta.integration) doc.integration = meta.integration as string
  if (meta.httpStatus != null) doc.httpStatus = meta.httpStatus as number
  if (meta.requestId) doc.requestId = meta.requestId as string
  if (meta.ip) doc.ip = meta.ip as string
  if (meta.userAgent) doc.userAgent = meta.userAgent as string
  if (meta.duration != null) doc.duration = meta.duration as number
  if (meta.data) doc.data = meta.data as Record<string, unknown>

  // Extract error info
  if (meta.error) {
    const err = meta.error
    if (err instanceof Error) {
      doc.errorMessage = err.message
      doc.errorStack = err.stack
      doc.errorType = err.constructor.name
    } else if (typeof err === 'string') {
      doc.errorMessage = err
    } else {
      doc.errorMessage = String(err)
    }
  }

  return doc
}

function getTrackingIndexName(stage: string): string {
  let normalizedStage = stage
  if (normalizedStage?.startsWith('dev')) {
    normalizedStage = 'dev'
  } else if (normalizedStage?.startsWith('test') || normalizedStage?.startsWith('staging')) {
    normalizedStage = 'master'
  }
  return `paintscout_logger_alias_${normalizedStage}`
}
